import React, { useEffect, useState } from 'react';
import OffcanvasPagosDuplicados from './OffcanvasPagosDuplicados';
import { Card, InputGroup, FormControl, Badge, Spinner } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useToast } from 'context/providers/ToastProvider';
import useToken from 'hooks/useToken';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';

const PagosReferenciasDuplicadas = ({ filters, setID_Pago, setShowModalConciliar, refreshTrigger }) => {
    const { token } = useToken();
    const { showErrorToast } = useToast();

    const [CargandoGetRefDuplicadas, setCargandoGetRefDuplicadas] = useState(true);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [referenciasDuplicadas, setReferenciasDuplicadas] = useState([]);

    // Estado del Offcanvas
    const [showOffcanvas, setShowOffcanvas] = useState(false);
    const [searchText, setSearchText] = useState('');

    // Paginación
    const [pageNumber, setPageNumber] = useState(1);
    const pageSize = 10;
    const [TotalRegistros, setTotalRegistros] = useState(0);

    // Estado para la referencia activa (seleccionada)
    const [activeRef, setActiveRef] = useState(null);

    // Debounce search
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (filters.month && filters.year) {
                ObtenerAlertaReferencias();
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [
        filters.month,
        filters.year,
        filters.idCatCuentaBancaria,
        filters.idCatMedioPago,
        filters.idVendedor,
        filters.filterValidados,
        filters.filterStatus,
        pageNumber,
        pageSize,
        searchText,
        refreshTrigger
    ]);

    const ObtenerAlertaReferencias = async () => {
        if (pageNumber === 1 && !isSearching && referenciasDuplicadas.length === 0 && !searchText) {
            setCargandoGetRefDuplicadas(true);
        } else if (pageNumber > 1) {
            setIsFetchingMore(true);
        }

        const response = await fetch(`/api/AdministracionPagos/ObtenerReferenciasDuplicadasList?pageNumber=${pageNumber}&pageSize=${pageSize}&searchText=${encodeURIComponent(searchText)}`, {
            method: 'GET',
            headers: {
                'Authorization': 'Bearer ' + token
            }
        });

        if (response.ok) {
            const responseType = await response.json();
            if (responseType.iResponseType === 1) {
                const responseData = JSON.parse(responseType.detalles);
                const data = responseData.Data || [];
                const total = responseData.TotalRegistros || 0;

                if (pageNumber === 1) {
                    setReferenciasDuplicadas(data);
                    setTotalRegistros(total);
                } else {
                    setReferenciasDuplicadas(prev => {
                        // Evitar duplicados si por error se llama 2 veces la misma página
                        const newRefs = data.filter(d => !prev.find(p => p.vReferencia === d.vReferencia));
                        return [...prev, ...newRefs];
                    });
                }

                if (pageNumber === 1 && data.length === 0) {
                    setActiveRef(null);
                }
            } else {
                showErrorToast(responseType.vMessage);
            }
        }
        setCargandoGetRefDuplicadas(false);
        setIsFetchingMore(false);
        setIsSearching(false);
    };

    // Calcular paginación con total de base de datos
    const totalPages = Math.ceil(TotalRegistros / pageSize);

    if (CargandoGetRefDuplicadas) {
        return <Card className="mb-3"><Card.Body><Skeleton height={50} count={2} /></Card.Body></Card>;
    }

    if (referenciasDuplicadas.length === 0 && !searchText && !isSearching) {
        return null; // Ocultar si no hay duplicadas y no se está buscando
    }

    return (
        <Card className="mb-3 border-danger-subtle">
            <Card.Header
                className="bg-danger-subtle d-flex align-items-center justify-content-between p-3"
                style={{ backgroundColor: '#fff5f5' }}
            >
                <div className="d-flex align-items-center gap-2">

                    <h5 className="mb-0 fw-bold" style={{ color: '#a11f2c' }}> Referencias Repetidas</h5>
                    <Badge bg="danger" pill className="ms-2">{TotalRegistros} Detectadas</Badge>
                </div>
            </Card.Header>

            <div>
                <Card.Body className="pt-3">
                    <InputGroup size="sm" className="mb-3" style={{ maxWidth: '300px' }}>
                        <InputGroup.Text className="bg-white text-500">
                            <FontAwesomeIcon icon="search" />
                        </InputGroup.Text>
                        <FormControl
                            placeholder="Buscar referencia..."
                            value={searchText}
                            onChange={(e) => {
                                setSearchText(e.target.value);
                                setPageNumber(1);
                                setIsSearching(true);
                            }}
                            className="shadow-none border-start-0 ps-0"
                        />
                    </InputGroup>

                    {isSearching ? (
                        <div className="d-flex justify-content-center align-items-center py-4">
                            <Spinner animation="border" variant="danger" />
                        </div>
                    ) : referenciasDuplicadas.length === 0 && searchText ? (
                        <div className="text-center py-4 text-500 fs--1">
                            No se encontraron referencias para "{searchText}"
                        </div>
                    ) : (
                        <div
                            className="d-flex gap-2 pb-2"
                            style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}
                            onScroll={(e) => {
                                const { scrollLeft, scrollWidth, clientWidth } = e.target;
                                // Si llegamos al final del scroll horizontal y no estamos cargando
                                if (Math.ceil(scrollLeft + clientWidth) >= scrollWidth - 10 && !isFetchingMore) {
                                    if (pageNumber < totalPages) {
                                        setPageNumber(prev => prev + 1);
                                    }
                                }
                            }}
                        >
                            {referenciasDuplicadas.map((ref, i) => (
                                <Card
                                    key={i}
                                    className={`flex-shrink-0 cursor-pointer shadow-sm ${activeRef === ref.vReferencia ? 'border-danger' : 'border-200'}`}
                                    style={{
                                        width: '160px',
                                        borderWidth: activeRef === ref.vReferencia ? '2px' : '1px',
                                        borderStyle: 'solid',
                                        backgroundColor: activeRef === ref.vReferencia ? '#fffaf8' : '#fff',
                                        transition: 'all 0.2s ease-in-out'
                                    }}
                                    onClick={() => {
                                        setActiveRef(ref.vReferencia);
                                        setShowOffcanvas(true);
                                    }}
                                >
                                    <Card.Body className="p-2 text-center d-flex flex-column justify-content-center">
                                        <div className="d-flex justify-content-center gap-1 mb-2">
                                            <Badge bg="danger" className="fs--2">
                                                [{ref.cantRef}]
                                            </Badge>
                                            {ref.cantCancelados > 0 && (
                                                <Badge bg="dark" className="fs--2" title={`${ref.cantCancelados} pagos cancelados`}>
                                                    <FontAwesomeIcon icon="ban" /> {ref.cantCancelados}
                                                </Badge>
                                            )}
                                        </div>
                                        <div
                                            className="text-truncate fw-bold fs--1 text-danger"
                                            title={ref.vReferencia}
                                        >
                                            {ref.vReferencia}
                                        </div>
                                    </Card.Body>
                                </Card>
                            ))}

                            {isFetchingMore && (
                                <div className="d-flex justify-content-center align-items-center" style={{ width: '80px', flexShrink: 0 }}>
                                    <Spinner animation="border" variant="danger" size="sm" />
                                </div>
                            )}
                        </div>
                    )}
                </Card.Body>
            </div>

            <OffcanvasPagosDuplicados
                show={showOffcanvas}
                onHide={() => setShowOffcanvas(false)}
                referencia={activeRef}
                token={token}
                setShowModalConciliar={setShowModalConciliar}
                setID_Pago={setID_Pago}
                refreshTrigger={refreshTrigger}
            />
        </Card>
    );
};

PagosReferenciasDuplicadas.propTypes = {
    filters: PropTypes.object,
    setID_Pago: PropTypes.func.isRequired,
    setShowModalConciliar: PropTypes.func.isRequired,
    refreshTrigger: PropTypes.number
};

export default PagosReferenciasDuplicadas;
